import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, ErrorState, Loading } from './states';

interface RegistrationView {
  state: string;
  title: string;
  explanation: string;
  recoverySteps: string[];
  severity: 'ok' | 'info' | 'attention' | 'urgent';
  dataPlaneUnaffected: boolean;
  lifecycle: string;
  canTransitionTo: string[];
}

const SEVERITY_VARIANT: Record<RegistrationView['severity'], 'info' | 'warning' | 'error'> = {
  ok: 'info',
  info: 'info',
  attention: 'warning',
  urgent: 'error',
};

/**
 * Registration state for one device.
 *
 * The state, why it is that way, and what to do about it are shown together.
 * An operator who sees "Disconnected" and nothing else has to guess whether
 * traffic is down; the reassurance that it is not is the most useful thing on
 * the panel, so it is not hidden behind a tooltip.
 *
 * The available transitions come from the server. The buttons follow what the
 * backend will accept rather than deciding it here — hiding a button is not
 * authorization, and the backend refuses illegal transitions regardless.
 */
export function RegistrationPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<RegistrationView>(`/edge-devices/${deviceId}/registration`),
    [deviceId],
  );
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setBusy(false);
    }
  };

  const transition = (lifecycle: string): Promise<void> =>
    act(() => api.post(`/edge-devices/${deviceId}/registration/transition`, { lifecycle }));

  const setQuarantine = (quarantined: boolean): Promise<void> =>
    act(() => api.post(`/edge-devices/${deviceId}/registration/quarantine`, { quarantined }));

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error.message} onRetry={reload} />;
  if (!data) return <ErrorState message={t('errors.notFound')} />;

  const quarantined = data.state === 'QUARANTINED';

  return (
    <div className="card">
      <h2>{t('registration.title')}</h2>

      <Banner variant={SEVERITY_VARIANT[data.severity]}>
        <strong>{data.title}</strong>
        <div>{data.explanation}</div>
      </Banner>

      {data.dataPlaneUnaffected && data.severity !== 'ok' ? (
        <p className="muted">{t('registration.dataPlaneUnaffected')}</p>
      ) : null}

      {data.recoverySteps.length > 0 ? (
        <>
          <h3>{t('registration.recoverySteps')}</h3>
          <ul>
            {data.recoverySteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
        </>
      ) : null}

      {actionError ? <Banner variant="error">{actionError}</Banner> : null}

      <div className="row-actions">
        {data.canTransitionTo.includes('SUSPENDED') ? (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => void transition('SUSPENDED')}
          >
            {t('registration.suspend')}
          </button>
        ) : null}
        {data.lifecycle === 'SUSPENDED' && data.canTransitionTo.includes('REGISTERED') ? (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => void transition('REGISTERED')}
          >
            {t('registration.resume')}
          </button>
        ) : null}
        {data.canTransitionTo.includes('REVOKED') ? (
          <button
            type="button"
            className="btn btn--ghost btn--danger"
            disabled={busy}
            onClick={() => void transition('REVOKED')}
          >
            {t('registration.revoke')}
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy}
          onClick={() => void setQuarantine(!quarantined)}
        >
          {quarantined ? t('registration.quarantineRelease') : t('registration.quarantine')}
        </button>
      </div>
    </div>
  );
}
